import { FC, useMemo, useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import { BvIcon } from 'bevi-icon';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import { SIM_TIPO_OPTIONS, SimTipoOption } from './constants';
import { applyMoneyMask, parseMoney } from './moneyUtils';
import { calculatePrazoRange } from './prazoCalculations';
import FinanceireLimitsHint from './FinanceireLimitsHint';
import UltimaSimulacaoCard from './UltimaSimulacaoCard';
import { selectStyles } from './selectStyles';

const PARAMS_CACHE_KEY = '@beviajuda:sim-step4-params';

const saveParamsCache = (simTipo: SimTipoOption, valor: string, parcelas: string) => {
    try {
        localStorage.setItem(PARAMS_CACHE_KEY, JSON.stringify({ simTipo, valor, parcelas }));
    } catch {}
};

const loadParamsCache = () => {
    try {
        const raw = localStorage.getItem(PARAMS_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

interface Step4ParamsProps {
    onSimulate: () => void;
    onBack: () => void;
    shouldLoadCache?: boolean;
}

const Step4Params: FC<Step4ParamsProps> = ({ onSimulate, onBack, shouldLoadCache = false }) => {
    const { financeiras, setSimParams, handleSimulate, isLoadingSimulation, ultimaSimulacao } = useSimulationBank();

    const [simTipo, setSimTipo] = useState<SimTipoOption>(SIM_TIPO_OPTIONS[0]);
    const [valor, setValor] = useState('');
    const [parcelas, setParcelas] = useState('');

    useEffect(() => {
        if (shouldLoadCache) {
            const cached = loadParamsCache();
            if (cached) {
                setSimTipo(cached.simTipo || SIM_TIPO_OPTIONS[0]);
                setValor(cached.valor || '');
                setParcelas(cached.parcelas || '');
            }
        }
    }, [shouldLoadCache]);

    useEffect(() => {
        saveParamsCache(simTipo, valor, parcelas);
    }, [simTipo, valor, parcelas]);

    const selecionadas = financeiras.filter((f) => f.selecionada);
    const isTotal = simTipo.value === 'total';
    const isDisabled = isLoadingSimulation || !parcelas || !valor;
    const { prazoMin, prazoMax } = useMemo(
        () => calculatePrazoRange(selecionadas),
        [selecionadas]
    );

    const handleSimTipoChange = (option: SimTipoOption | null) => {
        if (option) {
            setSimTipo(option);
            setValor('');
        }
    };

    const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValor(applyMoneyMask(e.target.value));
    };

    const handleSubmit = async () => {
        const params = {
            financeiras: selecionadas.map((f) => f.canonical_id ?? String(f.id)),
            num_parcelas: parseInt(parcelas, 10),
            ...(isTotal
                ? { valor_desejado: parseMoney(valor) }
                : { valor_parcela: parseMoney(valor) }),
        };
        setSimParams(params);
        onSimulate();
        await handleSimulate(params);
    };

    return (
        <div className="step-params">
            {ultimaSimulacao && <UltimaSimulacaoCard ultimaSimulacao={ultimaSimulacao} />}

            <p className="step-params__lead">
                Por favor, preencha os campos abaixo para seguir com a sua simulação.
            </p>

            <div className="fgroup">
                <label style={{ color: '#787ABA' }}>
                    Tipo de simulação <span className="req">*</span>
                </label>
                <ReactSelect
                    options={SIM_TIPO_OPTIONS}
                    value={simTipo}
                    styles={selectStyles}
                    placeholder="Selecione..."
                    noOptionsMessage={() => 'Sem opções'}
                    onChange={handleSimTipoChange}
                    isSearchable={false}
                />
            </div>

            <div className="fgroup">
                <label style={{ color: '#787ABA' }}>
                    {isTotal ? 'Valor total desejado' : 'Valor de parcela'}
                    <span className="req"> *</span>
                </label>
                <input
                    type="text"
                    className="step-params__input"
                    placeholder="0.000,00"
                    value={valor}
                    onChange={handleValorChange}
                />
                <FinanceireLimitsHint financeiras={selecionadas} isTotal={isTotal} />
            </div>

            <div className="fgroup">
                <label style={{ color: '#787ABA' }}>
                    Parcelas <span className="req">*</span>
                </label>
                <input
                    type="text"
                    className="step-params__input"
                    placeholder="Digite a quantidade de parcelas"
                    value={parcelas}
                    onChange={(e) => setParcelas(e.target.value.replace(/\D/g, ''))}
                />
                <span className="step-params__hint">
                    {Math.floor(prazoMin)} a {Math.ceil(prazoMax)}*
                </span>
            </div>

            <div className="step-params__info">
                <BvIcon name="info" variant="dark" style={{ color: '#222343' }} width={16} height={16} />
                Os valores podem ser adaptados aos limites de cada financeira.
            </div>

            <StepNavigation
                onBack={onBack}
                onNext={handleSubmit}
                nextLabel="Simular"
                nextDisabled={isDisabled}
                nextLoading={isLoadingSimulation}
            />
        </div>
    );
};

export default Step4Params;
